<template>
  <tile class="opc-zpi">
    <foldable v-model="isExpanded" :title="title">
      <template #head>
      <div class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name">
            {{ title }}
          </h2>
          <div v-if="!isExpanded" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li v-if="selectZPI">
                {{ getPlanCollapseText(selectZPI) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      </template>
      <div class="divide-line"></div>
      <div class="opc-zpi__main">
        <zm-form>
          <p class="opc-zpi__desc">
            {{ $t('billing.pbx.plan_desc.ZPI') }}
            <zm-tooltip
              class="opc-zpi__desc-countries"
              placement="top"
              disallow-learn-about
              aria-label="learn more about countries/territory"
              :content="$t('billing.pbx.intl_select_addon_countries')"
              @show="showSupportCountry"
            >
              <zm-button data-testid="checkout-button-241" type="text">
                {{ $t('billing.pbx.see_supported_country', [19]) }}
              </zm-button>
            </zm-tooltip>
          </p>
          <zm-form-item class="horizontal-form-item">
            <InputNumberWithPopover v-model="form.number" :min="minHost" :max="maxHost" :decrease-tips="decreaseTips" @change="handleNumberChange" label="International Calling Add-On Select the number of users"
              :disabled="disabled" size="lg" controls-position="both"
              :data-testid="`checkout-input-license-${defaultZPI ? defaultZPI.plan_code : ''}`"
            />
            <template>
              <span v-if="is_desktop_reactive || is_tablet_reactive" class="opc-zpi__unit">
                {{ getPlanUnitByQuantity(form.number) }}
                <span v-html="'\u00d7'"></span>
                {{$money(amount) }}{{ unit }}<priceVATTip />
              </span>
              <div v-else class="opc-zpi__unit">
                <p class="opc-zpi__unit--license">{{ getPlanUnitByQuantity(form.number) }}</p>
                <span class="opc-zpi__unit--price-unit">{{ $money(amount) }}{{ unit }}<priceVATTip /></span>
              </div>
            </template>
          </zm-form-item>
        </zm-form>
        <div class="opc-zm-base-card__main__footer">
          <div class="opc-zm-base-card__btn--wrap">
            <zm-popover :disabled='!disableDeleteTips' focus-trap placement='top' tabindex="-1" trigger="hover">
              <template #content>
                <div v-html='disableDeleteTips'></div>
              </template>
              <zm-button data-testid="checkout-button-242" :disabled='disabled || disableDeleteTips' type='text' @click='removePlanByKey'
                          :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}
              </zm-button>
            </zm-popover>
          </div>
        </div>
      </div>
    </foldable>
  </tile>
</template>
<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import { getPlanLogo } from '@/common/plan-logo-map';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { mapGetters, mapMutations, mapState, mapActions } from 'vuex';
import { computedCallingPlans } from '@/common/service/computers/plans/pbx';
import { BILLING_CYCLE, contactUsPath, enableP2PBuyMorePhoneLicense, planTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { flowTypes } from '@/common/service/page-config';
import { get } from 'lodash';
import { getPlanCollapseText } from '@/common/product';
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';

export default {
  data() {
    return {
      logo: getPlanLogo(planTypes.ZOOM_PHONE$ZPI),
      title: this.$t('billing.pbx.plan_name.ZPI'),
      isExpanded: true,
      form: {
        number: 0,
      },
    };
  },
  mixins: [plansMixin],
  components: {
    Foldable,
    Tile,
    InputNumberWithPopover,
  },
  created() {
    this.initConfigCart();
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters({
      'getSelectedPlans': 'getSelectedPlans',
      'getUserCurrentSubscriptions': 'getUserCurrentSubscriptions',
      'plancodeMapSubscriptionDisabled': 'plancodeMapSubscriptionDisabled',
      'boughtPlans': 'boughtPlans',
      'basicConfig': 'basicConfig',
    }),
    disabled() {
      const planCode = this.selectZPI?.plan_code;
      return !!this.plancodeMapSubscriptionDisabled[planCode];
    },
    allPlans() {
      return this.planMapFlat.filter(item => item.type === planTypes.ZOOM_PHONE$ZPI);
    },
    isStandardPrice() {
      const isEditFlow = this.$context.flow === flowTypes.edit;
      return (isEditFlow && !this.hasAccountDiscount) ? this.boughtZPI?.isStandardPrice : true;
    },
    minHost() {
      if (this.$context.flow === flowTypes.edit && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return get(this.boughtZPI, 'cartInfo.selected_number', 1);
      }
      return 1;
    },
    maxHost() {
      if (this.$context.flow === flowTypes.edit && enableP2PBuyMorePhoneLicense) {
        return 250;
      }
      return 99;
    },
    decreaseTips() {
      if (this.selectZPI?.cartInfo?.selected_number <= this.boughtZPI?.cartInfo?.selected_number) {
        if (this.basicConfig.downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
    currentBillingCycle() {
      const selectCallingPlan = computedCallingPlans(this.getUserCurrentSubscriptions)[0];
      if (selectCallingPlan) {
        return selectCallingPlan.billing_cycle;
      }
      return BILLING_CYCLE.ANNUAL;
    },
    cycleMap() {
      return {
        [BILLING_CYCLE.MONTHLY]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.MONTHLY),
        [BILLING_CYCLE.ANNUAL]: this.allPlans.find(item => item.billing_cycle === BILLING_CYCLE.ANNUAL),
      }
    },
    boughtZPI() {
      return this.boughtPlans.find((p) => p.plan_code === this.selectZPI?.plan_code);
    },
    selectZPI() {
      return this.getSelectedPlans.find(item => item.type === planTypes.ZOOM_PHONE$ZPI);
    },
    defaultZPI() {
      if (this.selectZPI) {
        return this.selectZPI;
      }
      return this.allPlans.find(item => item.billing_cycle === this.currentBillingCycle) || this.allPlans[0];
    },
    amount() {
      if (!this.defaultZPI) {
        return 0;
      }
      return this.defaultZPI.amount;
    },
    unit() {
      return this.$t(`billing.common.per_billing_cycle.${this.defaultZPI.billing_cycle}`)
    },
    disableDeleteTips() {
      const hasBought = this.boughtPlans.find((p) => p.plan_code === this.selectZPI?.plan_code);
      if ((hasBought && this.basicConfig?.isDirectSales) || hasBought?.isDisablePlanChangeOnline || hasBought?.ratePlans?.length > 1) {
        return this.$t('billing.cancel_disable_contact_us', {
          url: contactUsPath,
        })
      }
      return null;
    },
  },
  methods: {
    ...mapActions([ 'switchSelectPlan']),
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
    }),
    initConfigCart() {
      if (this.selectZPI) {
        this.form.number = this.selectZPI.cartInfo.selected_number;
        return;
      }
      this.form.number = 1;
      this.handleNumberChange(1);
    },
    handleNumberChange(number, oldValue) {
      if (oldValue && oldValue > number) {
        this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - decrease - ${number}`);
      } else if (oldValue && oldValue < number) {
        this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - increase - ${number}`);
      }
      if (!this.defaultZPI) return;
      this.updateSelectPlan({
        plan_code: this.defaultZPI.plan_code,
        cartInfo: { selected_number: number },
      });
    },
    switchBillingCycle() {
      if (!this.selectZPI) {
        this.initConfigCart()
      }
      const nextZPI = this.cycleMap[this.currentBillingCycle];
      if (!nextZPI) return;
      if (this.selectZPI.plan_code === nextZPI.plan_code) return;
      this.switchSelectPlan([this.selectZPI.plan_code, nextZPI.plan_code, this.selectZPI.cartInfo])
    },
    showSupportCountry() {
      this.$tracking.buyInteraction('zoom phone add-ons', `International Calling Add-On - see list of supported 19 countries/territory`);
    },
    getPlanUnitByQuantity(quantity) {
      return getPlanUnitByQuantity(planUnitMap.license, quantity);
    },
    getPlanCollapseText(plan) {
      const { amount, billing_cycle, cartInfo, cycleValue } = plan;
      return getPlanCollapseText({
        amount,
        billingCycle: billing_cycle,
        quantity: cartInfo.selected_number,
        cycleValue,
        unit: planUnitMap.license,
      });
    },
  },
  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `Zoom Phone International Calling Add-On - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
    currentBillingCycle(v, ov) {
      if (v !== ov) {
        this.switchBillingCycle();
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-zpi {
  .opc-zm-base-card {
    &__name {
      @include media(mobile) {
        display: block;
        padding-right: 70px;
      }
    }
    &__name {
      font-style: normal;
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  }

  &__main {
    margin-top: 0;
    border-top: none;
    .zbo-form-item.horizontal-form-item {
      .zbo-form-item__widgets {
        display: flex;
        align-items: center;
      }
    }
    .zbo-form-item__label-wrapper {
      display: none;
    }
  }

  &__unit {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }

  &__desc {
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    margin-bottom: 16px;

    @include media(mobile) {
      &-countries {
        &.zbo-button.zbo-button--text {
          text-align: left;
        }
      }
    }
  }

  @include media(tablet, desktop) {
    &__unit {
      margin-left: 8px;
    }
  }

  @include media(mobile) {
    &__unit {
      margin-left: 8px;

      &--license {
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }

      &--price-unit {
        color: var(--zoom-color-text-strong-neutral, #555B62);
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
    }
  }
}
</style>
